import React, { useState, useEffect, useMemo } from 'react';
import {
  Panel,
  PanelType,
  Stack,
  TextField,
  DefaultButton,
  PrimaryButton,
  ComboBox,
  IComboBoxOption,
  Text,
  Icon,
  MessageBar,
  MessageBarType,
  IconButton,
  Spinner,
  CommandBar,
  ShimmeredDetailsList,
  Selection,
  SelectionMode,
} from '@fluentui/react';
import moment from 'moment';
import { useAuth0 } from '@auth0/auth0-react';
import { useLazyQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import useDebounce from '../../../components/hooks/useDebounce';
import { commandBarTheme, customPanelStyles } from '../../../theme';
import {
  DEFAULT_TOAST_DURATION,
  DEFAULT_TOAST_POSITION,
  GET_STORAGE_TOKEN,
  modalContentStyles,
} from '../../../utils';
import { toastError, toastSuccess } from '../../../utils/toast';
import { BlobStorage } from '../../blob-storage';
import { sendMailRequest, MailSendFailure } from '../../../utils/sendMail';
import { normalizeBillitInvoice } from '../billitLinkAmounts';
import { BillitInvoice, Employee, getActivePurchaseOrderLinks } from '../types';
import { createPdfObjectUrlFromUblXml } from '../extractPdfFromUblXml';
import { getWingsJournalMailForDate } from '../billitWingsMail';
import BillitPurchaseOrderLinks from './BillitPurchaseOrderLinks';
import BillitInvoiceDetailLayout from './BillitInvoiceDetailLayout';
import MailDetail from './MailDetail';

interface Props {
  isOpen: boolean;
  dismissPanel: any;
  invoice?: BillitInvoice;
  refetch?: any;
  onFastActionComplete?: () => void;
}

// Extra PDF's uit de UBL (leveringsbonnen, voorwaarden, …) zitten al in het UBL-bestand
// dat naar Wings gaat; ze apart meesturen zou ze in Wings dubbel geven.
const UBL_EXTRA_PDF_PATH = '/pdf/extra/';
const isMailableAttachment = (attachment: { blob_name?: string | null }) => !(
  attachment?.blob_name || ''
).includes(UBL_EXTRA_PDF_PATH);

const BillitInvoiceDetail = ({
  isOpen,
  dismissPanel,
  invoice,
  refetch,
  onFastActionComplete,
}: Props) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [detailedInvoice, setDetailedInvoice] = useState<BillitInvoice | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
    null,
  );
  const [assigning, setAssigning] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeRemarks, setEmployeeRemarks] = useState('');
  const [pdfDataUrl, setPdfDataUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [addingRemarks, setAddingRemarks] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [showApproveAfterRejectModal, setShowApproveAfterRejectModal] = useState(false);
  const [approveAfterRejectRemarks, setApproveAfterRejectRemarks] = useState('');
  const [approvingAfterReject, setApprovingAfterReject] = useState(false);
  const [attachmentRefreshKey, setAttachmentRefreshKey] = useState(0);
  const [isMailPanelOpen, setIsMailPanelOpen] = useState(false);
  const [mail, setMail] = useState<any>();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [sendingMail, setSendingMail] = useState(false);
  const [selectionMailDetails, setSelectionMailDetails] = useState<any>();
  const debouncedEmployeeSearch = useDebounce(employeeSearch, 500);

  const selectionMail = useMemo(() => {
    const selection = new Selection({
      onSelectionChanged: () => {
        const current = selection.getSelection();
        setSelectionMailDetails(current.length > 0 ? current[0] : undefined);
      },
    });
    return selection;
  }, []);

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      if (!invoice || !isOpen) return;

      try {
        setLoading(true);
        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setDetailedInvoice(normalizeBillitInvoice(data));
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to fetch invoice details';
        toastError(errorMessage);
        setDetailedInvoice(invoice);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceDetails();
  }, [invoice, isOpen, getAccessTokenSilently]);

  useEffect(() => {
    const fetchEmployees = async () => {
      if (!isOpen) return;

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URI}/billit/employees-with-app-users`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          setEmployees(data || []);
        }
      } catch (error) {
        // Silently fail - employees list is optional
      }
    };

    fetchEmployees();
  }, [isOpen, getAccessTokenSilently]);

  useEffect(() => {
    if (detailedInvoice?.assigned_employee_id) {
      setSelectedEmployeeId(detailedInvoice.assigned_employee_id);
    } else if (invoice?.assigned_employee_id) {
      setSelectedEmployeeId(invoice.assigned_employee_id);
    } else {
      setSelectedEmployeeId(null);
    }
  }, [detailedInvoice, invoice]);

  const convertEmployeesToOptions = (): IComboBoxOption[] =>
    employees.map(emp => ({
      key: emp.id,
      text: `${emp.first_name} ${emp.last_name}`.trim() || '-',
    }));

  // Filter employees based on search (client-side filtering)
  const filteredEmployees = React.useMemo(() => {
    if (!debouncedEmployeeSearch || !showEmployeeModal) {
      return employees;
    }
    const searchLower = debouncedEmployeeSearch.toLowerCase();
    return employees.filter(
      emp =>
        emp.first_name.toLowerCase().includes(searchLower) ||
        emp.last_name.toLowerCase().includes(searchLower),
    );
  }, [employees, debouncedEmployeeSearch, showEmployeeModal]);

  // Reset modals when closing
  useEffect(() => {
    if (!showEmployeeModal) {
      setEmployeeSearch('');
      setEmployeeRemarks('');
      setSelectedEmployeeId(null);
    }
  }, [showEmployeeModal]);

  // When opening allocatie modal: suggest werfleider of the first linked werf as preferred employee
  useEffect(() => {
    if (!showEmployeeModal || !employees.length) return;
    const activeLinks = getActivePurchaseOrderLinks(detailedInvoice);
    const siteManager = activeLinks[0]?.purchase_order?.construction_site?.site_manager;
    if (!siteManager?.id) return;
    const alreadyAssigned = detailedInvoice?.assigned_employee_id ?? invoice?.assigned_employee_id;
    if (alreadyAssigned) return;
    const isInList = employees.some(e => e.id === siteManager.id);
    if (isInList) {
      setSelectedEmployeeId(siteManager.id);
    }
  }, [
    showEmployeeModal,
    detailedInvoice?.purchase_order_links,
    detailedInvoice?.assigned_employee_id,
    invoice?.assigned_employee_id,
    employees,
  ]);

  const handleFastApprove = async () => {
    if (!invoice) return;

    try {
      setApproving(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}/fast-approve`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDetailedInvoice(data);
      toastSuccess('Factuur goedgekeurd');
      setRemarks('');

      // Bewust géén onFastActionComplete(): dat sluit het paneel en springt terug
      // naar het hoofdscherm. Na goedkeuren is versturen de logische volgende stap,
      // dus we blijven staan en openen meteen het mailvenster. `data` gaat mee,
      // want `invoiceData` draagt in deze render nog de oude status.
      refetch?.();
      await openMailDetail(true, data);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to approve invoice';
      toastError(errorMessage);
    } finally {
      setApproving(false);
    }
  };

  const handleApproveAfterReject = async () => {
    if (!invoice || !approveAfterRejectRemarks.trim()) return;

    try {
      setApprovingAfterReject(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}/approve`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ remarks: approveAfterRejectRemarks.trim() }),
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDetailedInvoice(data);
      setShowApproveAfterRejectModal(false);
      setApproveAfterRejectRemarks('');
      toastSuccess('Factuur alsnog goedgekeurd');
      if (refetch) {
        refetch();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to approve invoice';
      toastError(errorMessage);
    } finally {
      setApprovingAfterReject(false);
    }
  };

  const handleFastReject = async () => {
    if (!invoice || !rejectRemarks.trim()) return;

    try {
      setRejecting(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}/fast-reject`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ remarks: rejectRemarks.trim() }),
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDetailedInvoice(data);
      setShowRejectModal(false);
      setRejectRemarks('');
      toastSuccess('Factuur afgekeurd');
      onFastActionComplete?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to reject invoice';
      toastError(errorMessage);
    } finally {
      setRejecting(false);
    }
  };

  const handleAssignToEmployee = async () => {
    if (!invoice || !selectedEmployeeId || !employeeRemarks.trim()) return;

    try {
      setAssigning(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}/assign-employee`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            employee_id: selectedEmployeeId,
            remarks: employeeRemarks,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDetailedInvoice(data);
      setShowEmployeeModal(false);
      toastSuccess('Factuur toegewezen aan medewerker');
      if (refetch) {
        refetch();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to assign invoice';
      toastError(errorMessage);
    } finally {
      setAssigning(false);
    }
  };

  const handleAddRemarks = async () => {
    if (!invoice || !remarks.trim()) return;

    try {
      setAddingRemarks(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}/remarks`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            remarks: remarks.trim(),
            is_from_management: true, // This is called from management view
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Refetch invoice to get updated logs
      const invoiceResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (invoiceResponse.ok) {
        const data = await invoiceResponse.json();
        setDetailedInvoice(data);
      }

      setShowRemarksModal(false);
      setRemarks('');
      toastSuccess('Opmerking toegevoegd');
      if (refetch) {
        refetch();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to add remarks';
      toastError(errorMessage);
    } finally {
      setAddingRemarks(false);
    }
  };

  const parseJsonField = (jsonString: string | null | undefined) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  };

  const getSupplierInfo = () => {
    const invoiceData = detailedInvoice || invoice;
    if (!invoiceData) return null;

    return parseJsonField(invoiceData.supplier_json);
  };

  const supplierInfo = getSupplierInfo();
  const invoiceData = detailedInvoice || invoice;

  useEffect(() => {
    const currentInvoiceData = detailedInvoice || invoice;
    if (!currentInvoiceData?.ubl_xml_content) {
      setPdfDataUrl(null);
      setPdfLoading(false);
      return;
    }

    setPdfLoading(true);
    setPdfError(null);

    const url = createPdfObjectUrlFromUblXml(currentInvoiceData.ubl_xml_content);
    setPdfDataUrl(url);
    setPdfLoading(false);
    if (!url) {
      setPdfError(null);
    }
  }, [detailedInvoice?.ubl_xml_content, invoice?.ubl_xml_content]);

  // Separate cleanup effect for when component unmounts or pdfDataUrl changes
  useEffect(
    () => () => {
      // Cleanup previous URL when pdfDataUrl changes or component unmounts
      if (pdfDataUrl) {
        URL.revokeObjectURL(pdfDataUrl);
      }
    },
    [pdfDataUrl],
  );

  const existingMailAttachments = useMemo(
    () => (invoiceData?.billit_invoice_attachments || []).filter(isMailableAttachment).map(attachment => ({
      name: attachment.blob_name,
      originalName: attachment.original_name,
      contentType: attachment.content_type || undefined,
      extension: attachment.original_name.split('.').pop()?.toLowerCase(),
      url: `https://3bouw.blob.core.windows.net/attachments/${attachment.blob_name}`,
    })),
    [invoiceData?.billit_invoice_attachments],
  );

  /**
   * `freshInvoice` laat de aanroeper een net opgehaalde factuur meegeven. Nodig
   * na 'snel goedkeuren': `setDetailedInvoice` is dan al aangeroepen, maar
   * `invoiceData` wijst in deze render nog naar de oude waarde met de vorige
   * status. Zonder die override zou de controle hieronder de mail blokkeren op
   * een factuur die zonet goedgekeurd is.
   */
  const openMailDetail = async (newMail?: boolean, freshInvoice?: BillitInvoice) => {
    const invoiceForMail = freshInvoice || invoiceData;

    if (!invoiceForMail || invoiceForMail.order_status !== 'approved') {
      toastError('Alleen goedgekeurde facturen kunnen verstuurd worden');
      return;
    }

    // Bijlagen ALTIJD vers ophalen, niet enkel wanneer de lijst leeg is.
    // De lijst in het geheugen dateert van het openen van het paneel en wordt
    // daarna niet meer bijgewerkt: AttachmentList ververst enkel zichzelf, en na
    // het koppelen van een bestelbon geeft de backend de factuur zonder
    // bijlagenveld terug. Een bestand dat ondertussen is toegevoegd, miste
    // daardoor de mail. Alles moet mee naar Wings, dus hier geen aannames.
    let attachmentsForMail = invoiceForMail.billit_invoice_attachments || [];
    if (invoiceForMail.billit_order_id) {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoiceForMail.billit_order_id}/attachments`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (response.ok) {
          const verse = await response.json();
          // Alleen overnemen als de ophaling iets oplevert; een lege of mislukte
          // respons mag een lijst die we al hadden niet wissen.
          if (Array.isArray(verse) && verse.length) {
            attachmentsForMail = verse.filter(isMailableAttachment);
            setDetailedInvoice(prev => (
              prev
                ? { ...prev, billit_invoice_attachments: verse }
                : prev
            ));
          }
        }
      } catch {
        // val terug op wat we al hadden; de gebruiker kan nog handmatig toevoegen
      }
    }

    if (selectionMailDetails && !newMail) {
      setMail(selectionMailDetails);
    } else {
      setSelectedFiles([]);
      const suggestedJournal = getWingsJournalMailForDate(invoiceForMail.order_date);
      const suggestedTo = suggestedJournal?.email || '';
      setMail({
        to: suggestedTo ? JSON.stringify([suggestedTo]) : '',
        subject: `Factuur ${invoiceForMail.order_number || invoiceForMail.billit_order_id}`,
        from: user?.email || '',
        body: `<p>Beste,</p><p>In bijlage vindt u factuur ${
          invoiceForMail.order_number || invoiceForMail.billit_order_id
        }.</p>`,
        attachments: JSON.stringify(
          attachmentsForMail.filter(isMailableAttachment).map((attachment: any) => ({
            name: attachment.blob_name,
            originalName: attachment.original_name,
            contentType: attachment.content_type || undefined,
            extension: attachment.original_name?.split('.').pop()?.toLowerCase(),
            url: `https://3bouw.blob.core.windows.net/attachments/${attachment.blob_name}`,
          })),
        ),
      });
    }
    setIsMailPanelOpen(true);
  };

  const sendMail = async (attachments: any[]) => {
    const token = await getAccessTokenSilently();
    if (
      !invoiceData?.id
      || !token
      || !user?.email
      || !(user.email.includes('@3bouw') || user.email.includes('@ismart'))
    ) {
      throw new MailSendFailure('unknown', 'Gebruiker niet gevonden. De mail is NIET verstuurd.');
    }

    // Gooit wanneer er niets verstuurd is; alles hieronder draait dus enkel na
    // een bevestigde verzending naar Wings.
    await sendMailRequest(token, {
      from: user.email,
      to: mail.to,
      cc: mail.cc,
      bcc: mail.bcc,
      subject: mail.subject,
      body: mail.body,
      attachments: JSON.stringify(attachments),
      billit_invoice_id: invoiceData.id,
    });

    // De mail IS hier verstuurd. Alles wat volgt werkt enkel het scherm bij;
    // een fout daarin mag NOOIT als verzendfout getoond worden, anders zou een
    // gebruiker denken dat er niets vertrokken is terwijl dat wel zo is.
    try {
      setDetailedInvoice(prev => (prev ? { ...prev, sent: true } : prev));
      setSelectedFiles([]);
      setIsMailPanelOpen(false);
      toastSuccess('Email verstuurd');
      if (refetch) refetch();
      dismissPanel(true);
    } catch (uiError) {
      console.error('Mail verstuurd, maar het bijwerken van het scherm faalde:', uiError);
    }
  };

  // Bewust ZONDER onCompleted: fouten binnen die callback worden door Apollo
  // opgeslokt, waardoor een mislukte verzending nergens zichtbaar was.
  const [acquireToken] = useLazyQuery(GET_STORAGE_TOKEN, {
    fetchPolicy: 'no-cache',
  });

  /** Zet de handmatig toegevoegde bestanden in de opslag. */
  const uploadSelectedFiles = async (tokenData: any) => {
    const fileStore = new BlobStorage(tokenData.accountName, tokenData.sasToken);

    return Promise.all(
      selectedFiles.map(async (file) => {
        const fileName = `${Date.now()}_${file?.name}`;
        const buffer = await file.arrayBuffer();
        await fileStore.uploadBlob('attachments', fileName, buffer, buffer.byteLength);
        return {
          name: fileName,
          url: `https://${tokenData.accountName}.blob.core.windows.net/attachments/${fileName}`,
          extension: fileName.split('.').pop()?.toLowerCase(),
        };
      }),
    );
  };

  /** De bijlagen die al op de factuur staan (PDF, XML, die van bestelbonnen). */
  const getExistingMailAttachments = () => {
    const fromMailState = (() => {
      try {
        return JSON.parse(mail?.attachments || '[]');
      } catch {
        return [];
      }
    })();

    return (fromMailState.length > 0 ? fromMailState : existingMailAttachments).map(
      (att: any) => ({
        name: att.name,
        url: att.url,
        extension: att.extension,
        contentType: att.contentType,
      }),
    );
  };

  const saveMail = async () => {
    if (sendingMail) return;
    setSendingMail(true);
    try {
      const { data } = await acquireToken();
      if (!data?.createStorageToken) {
        throw new MailSendFailure('unknown', 'Geen toegang tot de bestandsopslag. De mail is NIET verstuurd.');
      }

      const uploaded = await uploadSelectedFiles(data.createStorageToken);
      await sendMail([...getExistingMailAttachments(), ...uploaded]);
    } catch (error) {
      // Doorgeven aan het mailvenster: dat toont de fout naast de Verzenden-knop,
      // waar de gebruiker kijkt. Een toast verdween achter het paneel.
      throw error instanceof MailSendFailure
        ? error
        : new MailSendFailure('unknown', 'Versturen van de mail is mislukt. De mail is NIET verstuurd.');
    } finally {
      setSendingMail(false);
    }
  };

  if (!invoiceData) return null;

  return (
    <Panel
      isLightDismiss
      isOpen={isOpen}
      onDismiss={dismissPanel}
      closeButtonAriaLabel='Close'
      headerText={`Factuur ${
        invoiceData.order_number || invoiceData.billit_order_id
      }`}
      type={PanelType.custom}
      customWidth='min(1280px, 96vw)'
      styles={{
        root: {
          '.ms-Panel-main': {
            marginRight: 'auto',
            marginLeft: 'auto',
          },
        },
        main: { maxWidth: '96vw' },
        scrollableContent: {
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        },
        content: {
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          paddingBottom: '0px',
        },
      }}
    >
      {/* Employee Assignment Panel */}
      {showEmployeeModal && (
        <Panel
          isLightDismiss
          isOpen={showEmployeeModal}
          onDismiss={() => setShowEmployeeModal(false)}
          closeButtonAriaLabel='Close'
          headerText='Toewijzen aan medewerker'
          type={PanelType.custom}
          customWidth='600px'
          styles={customPanelStyles}
        >
          <div>
            <div className={modalContentStyles.header} />
            <div className={modalContentStyles.body}>
              <Stack tokens={{ childrenGap: 16 }}>
                <TextField
                  label='Zoek medewerker'
                  value={employeeSearch}
                  onChange={(_, newValue) => setEmployeeSearch(newValue || '')}
                  placeholder='Naam'
                  iconProps={{ iconName: 'Search' }}
                />

                {/* Show search results in custom block list */}
                {!selectedEmployeeId && filteredEmployees.length > 0 && (
                  <Stack tokens={{ childrenGap: 8 }}>
                    <Text style={{ fontWeight: 600, fontSize: '14px' }}>
                      Selecteer een medewerker:
                    </Text>
                    <div
                      style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        border: '1px solid #E1E1E1',
                        borderRadius: '4px',
                      }}
                    >
                      {filteredEmployees.map(emp => (
                        <div
                          key={emp.id}
                          role='button'
                          tabIndex={0}
                          onClick={() => setSelectedEmployeeId(emp.id)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedEmployeeId(emp.id);
                            }
                          }}
                          style={{
                            padding: '12px',
                            borderBottom: '1px solid #E1E1E1',
                            cursor: 'pointer',
                            backgroundColor: 'white',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = '#f3f2f1';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          <Stack tokens={{ childrenGap: 4 }}>
                            <Text style={{ fontWeight: 600 }}>
                              {emp.first_name} {emp.last_name}
                            </Text>
                          </Stack>
                        </div>
                      ))}
                    </div>
                  </Stack>
                )}

                {/* Show selected employee details */}
                {selectedEmployeeId && (
                  <Stack tokens={{ childrenGap: 12 }}>
                    <Stack
                      horizontal
                      horizontalAlign='space-between'
                      verticalAlign='center'
                    >
                      <Text style={{ fontWeight: 600, fontSize: '16px' }}>
                        Geselecteerde medewerker
                      </Text>
                      <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        title='Deselecteren'
                        onClick={() => setSelectedEmployeeId(null)}
                        styles={{
                          root: {
                            color: '#666',
                          },
                          rootHovered: {
                            backgroundColor: '#f3f2f1',
                          },
                        }}
                      />
                    </Stack>

                    {(() => {
                      const selectedEmployee = filteredEmployees.find(
                        emp => emp.id === selectedEmployeeId,
                      ) || employees.find(emp => emp.id === selectedEmployeeId);
                      if (!selectedEmployee) return null;

                      return (
                        <div
                          style={{
                            border: '1px solid #71BF43',
                            borderRadius: '2px',
                            padding: '12px',
                            backgroundColor: '#d8eacc',
                          }}
                        >
                          <Stack tokens={{ childrenGap: 8 }}>
                            <Text style={{ fontWeight: 600 }}>
                              {selectedEmployee.first_name}{' '}
                              {selectedEmployee.last_name}
                            </Text>
                          </Stack>
                        </div>
                      );
                    })()}
                  </Stack>
                )}

                <TextField
                  label='Opmerkingen (verplicht)'
                  value={employeeRemarks}
                  onChange={(_, newValue) => setEmployeeRemarks(newValue || '')}
                  multiline
                  rows={5}
                  placeholder='Voer opmerkingen in voor de medewerker'
                  required
                />
              </Stack>
            </div>

            <div className={modalContentStyles.footer}>
              <Stack horizontal horizontalAlign='end' tokens={{ childrenGap: 10 }}>
                <DefaultButton onClick={() => setShowEmployeeModal(false)}>
                  Annuleren
                </DefaultButton>
                <PrimaryButton
                  onClick={handleAssignToEmployee}
                  disabled={
                    !selectedEmployeeId || !employeeRemarks.trim() || assigning
                  }
                >
                  Toewijzen
                </PrimaryButton>
              </Stack>
            </div>
          </div>
        </Panel>
      )}

      {/* Remarks Panel */}
      {showRemarksModal && (
        <Panel
          isLightDismiss
          isOpen={showRemarksModal}
          onDismiss={() => setShowRemarksModal(false)}
          closeButtonAriaLabel='Close'
          headerText='Opmerking toevoegen'
          type={PanelType.custom}
          customWidth='600px'
          styles={customPanelStyles}
        >
          <div>
            <div className={modalContentStyles.header} />
            <div className={modalContentStyles.body}>
              <Stack tokens={{ childrenGap: 16 }}>
                <TextField
                  label='Opmerkingen'
                  value={remarks}
                  onChange={(_, newValue) => setRemarks(newValue || '')}
                  multiline
                  rows={6}
                  placeholder='Voer opmerkingen in'
                />
              </Stack>
            </div>

            <div className={modalContentStyles.footer}>
              <Stack horizontal horizontalAlign='end' tokens={{ childrenGap: 10 }}>
                <DefaultButton onClick={() => setShowRemarksModal(false)}>
                  Annuleren
                </DefaultButton>
                <PrimaryButton
                  onClick={handleAddRemarks}
                  disabled={!remarks.trim() || addingRemarks}
                >
                  Toevoegen
                </PrimaryButton>
              </Stack>
            </div>
          </div>
        </Panel>
      )}

      {/* Snel afkeuren modal - opmerking verplicht */}
      {showRejectModal && (
        <Panel
          isLightDismiss
          isOpen={showRejectModal}
          onDismiss={() => { if (!rejecting) { setShowRejectModal(false); setRejectRemarks(''); } }}
          closeButtonAriaLabel='Sluiten'
          headerText='Snel afkeuren'
          type={PanelType.custom}
          customWidth='600px'
          styles={customPanelStyles}
        >
          <div>
            <div className={modalContentStyles.header} />
            <div className={modalContentStyles.body}>
              <Stack tokens={{ childrenGap: 16 }}>
                <TextField
                  label='Opmerking (verplicht)'
                  value={rejectRemarks}
                  onChange={(_, newValue) => setRejectRemarks(newValue || '')}
                  multiline
                  rows={5}
                  placeholder='Geef de reden van afkeuring'
                  required
                />
              </Stack>
            </div>
            <div className={modalContentStyles.footer}>
              <Stack horizontal horizontalAlign='end' tokens={{ childrenGap: 10 }}>
                <DefaultButton
                  onClick={() => { setShowRejectModal(false); setRejectRemarks(''); }}
                  disabled={rejecting}
                >
                  Annuleren
                </DefaultButton>
                <DefaultButton
                  onClick={handleFastReject}
                  disabled={!rejectRemarks.trim() || rejecting}
                  iconProps={{ iconName: 'Cancel' }}
                  styles={{
                    root: { borderColor: '#d13438', color: '#d13438' },
                    rootHovered: { borderColor: '#a4262c', color: '#a4262c' },
                  }}
                >
                  {rejecting ? 'Bezig...' : 'Afkeuren'}
                </DefaultButton>
              </Stack>
            </div>
          </div>
        </Panel>
      )}

      {/* Alsnog goedkeuren (rejected invoice) - opmerking verplicht */}
      {showApproveAfterRejectModal && (
        <Panel
          isLightDismiss
          isOpen={showApproveAfterRejectModal}
          onDismiss={() => {
            if (!approvingAfterReject) {
              setShowApproveAfterRejectModal(false);
              setApproveAfterRejectRemarks('');
            }
          }}
          closeButtonAriaLabel='Sluiten'
          headerText='Alsnog goedkeuren'
          type={PanelType.custom}
          customWidth='600px'
          styles={customPanelStyles}
        >
          <div>
            <div className={modalContentStyles.header} />
            <div className={modalContentStyles.body}>
              <Stack tokens={{ childrenGap: 16 }}>
                <TextField
                  label='Opmerking (verplicht)'
                  value={approveAfterRejectRemarks}
                  onChange={(_, newValue) => setApproveAfterRejectRemarks(newValue || '')}
                  multiline
                  rows={5}
                  placeholder='Bijv. creditnota ontvangen ter rechtzetting'
                  required
                />
              </Stack>
            </div>
            <div className={modalContentStyles.footer}>
              <Stack horizontal horizontalAlign='end' tokens={{ childrenGap: 10 }}>
                <DefaultButton
                  onClick={() => {
                    setShowApproveAfterRejectModal(false);
                    setApproveAfterRejectRemarks('');
                  }}
                  disabled={approvingAfterReject}
                >
                  Annuleren
                </DefaultButton>
                <PrimaryButton
                  onClick={handleApproveAfterReject}
                  disabled={!approveAfterRejectRemarks.trim() || approvingAfterReject}
                  iconProps={{ iconName: 'Accept' }}
                >
                  {approvingAfterReject ? 'Bezig...' : 'Goedkeuren'}
                </PrimaryButton>
              </Stack>
            </div>
          </div>
        </Panel>
      )}

      <div>
        <div className={modalContentStyles.header} />
        <div className={modalContentStyles.body}>
          <BillitInvoiceDetailLayout
            invoiceData={invoiceData}
            pdf={{ url: pdfDataUrl, loading: pdfLoading, error: pdfError }}
            supplierInfo={supplierInfo}
            attachmentRefreshKey={attachmentRefreshKey}
            onAddRemark={() => setShowRemarksModal(true)}
            topBanners={
              <>
                {invoiceData.order_status === 'rejected' ? (
                  <MessageBar
                    messageBarType={MessageBarType.error}
                    styles={{
                      root: {
                        backgroundColor: '#fde7e9',
                        borderLeft: '4px solid #d13438',
                        fontWeight: 600,
                      },
                    }}
                  >
                    Factuur afgewezen
                  </MessageBar>
                ) : null}
                {invoiceData.sent ? (
                  <MessageBar messageBarType={MessageBarType.success}>
                    Factuur is verstuurd per e-mail
                  </MessageBar>
                ) : null}
              </>
            }
            actionsBar={
              <>
                {invoiceData.order_status === 'approved' && (
                  <PrimaryButton
                    onClick={() => {
                      openMailDetail(true).catch(() => undefined);
                    }}
                    iconProps={{ iconName: 'Mail' }}
                  >
                    {invoiceData.sent ? 'Opnieuw mailen' : 'Mailen'}
                  </PrimaryButton>
                )}
                {invoiceData.order_status === 'rejected' && (
                  <PrimaryButton
                    onClick={() => setShowApproveAfterRejectModal(true)}
                    disabled={approvingAfterReject}
                    iconProps={{ iconName: 'Accept' }}
                  >
                    Alsnog goedkeuren
                  </PrimaryButton>
                )}
                {invoiceData.order_status !== 'approved' &&
                  invoiceData.order_status !== 'rejected' && (
                    <>
                      <PrimaryButton
                        onClick={handleFastApprove}
                        disabled={approving || rejecting}
                        iconProps={{ iconName: 'Accept' }}
                      >
                        Snel goedkeuren
                      </PrimaryButton>
                      <DefaultButton
                        onClick={() => setShowRejectModal(true)}
                        disabled={approving || rejecting}
                        styles={{
                          root: { borderColor: '#d13438', color: '#d13438' },
                          rootHovered: { borderColor: '#a4262c', color: '#a4262c' },
                        }}
                      >
                        Snel afkeuren
                      </DefaultButton>
                      <DefaultButton
                        onClick={() => setShowEmployeeModal(true)}
                        iconProps={{ iconName: 'UserAdd' }}
                      >
                        Toewijzen
                      </DefaultButton>
                    </>
                  )}
              </>
            }
            purchaseOrderLinks={
              <BillitPurchaseOrderLinks
                invoice={invoice!}
                detailedInvoice={invoiceData}
                setDetailedInvoice={updated => {
                  setDetailedInvoice(updated);
                  setAttachmentRefreshKey(prev => prev + 1);
                }}
                onLinksChanged={refetch}
                invoicePdfUrl={pdfDataUrl}
                invoicePdfLoading={pdfLoading}
              />
            }
          />

          {invoiceData.order_status === 'approved' && (
            <Stack tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 24 } }}>
              <Text variant='large' styles={{ root: { fontWeight: 600 } }}>Mails</Text>
              <CommandBar
                items={[
                  {
                    key: 'new',
                    text: 'Nieuw',
                    iconProps: { iconName: 'Add' },
                    onClick: () => {
                      openMailDetail(true).catch(() => undefined);
                    },
                    theme: commandBarTheme,
                  },
                  {
                    key: 'view',
                    text: 'Bekijk',
                    iconProps: { iconName: 'Edit' },
                    onClick: () => {
                      openMailDetail().catch(() => undefined);
                    },
                    theme: commandBarTheme,
                    disabled: !selectionMailDetails,
                  },
                ]}
                theme={commandBarTheme}
              />
              <ShimmeredDetailsList
                items={invoiceData.mail_logs || []}
                columns={[
                  {
                    key: 'from',
                    name: 'Van',
                    fieldName: 'from',
                    minWidth: 120,
                    maxWidth: 180,
                    onRender: (log: any) => <span>{log.from}</span>,
                  },
                  {
                    key: 'subject',
                    name: 'Onderwerp',
                    fieldName: 'subject',
                    minWidth: 160,
                    maxWidth: 280,
                    onRender: (log: any) => <span>{log.subject}</span>,
                  },
                  {
                    key: 'to',
                    name: 'Ontvangers',
                    fieldName: 'to',
                    minWidth: 160,
                    maxWidth: 260,
                    onRender: (log: any) => {
                      try {
                        return <span>{JSON.parse(log.to || '[]').join(', ')}</span>;
                      } catch {
                        return <span>{log.to}</span>;
                      }
                    },
                  },
                  {
                    key: 'date',
                    name: 'Datum',
                    fieldName: 'date_created',
                    minWidth: 120,
                    maxWidth: 160,
                    onRender: (log: any) => (
                      <span>
                        {log.date_created
                          ? moment(log.date_created).format('DD/MM/YYYY HH:mm')
                          : ''}
                      </span>
                    ),
                  },
                ]}
                selection={selectionMail}
                selectionMode={SelectionMode.single}
                onItemInvoked={() => {
                  openMailDetail().catch(() => undefined);
                }}
                enableShimmer={false}
              />
            </Stack>
          )}
        </div>

        {isMailPanelOpen && invoiceData && (
          <MailDetail
            key={`${invoiceData.id}-mail`}
            isOpen={isMailPanelOpen}
            dismissPanel={() => {
              setSelectedFiles([]);
              setIsMailPanelOpen(false);
            }}
            mail={mail}
            setMail={setMail}
            saveMail={saveMail}
            billitInvoice={invoiceData}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            existingAttachments={existingMailAttachments}
          />
        )}

        <div className={modalContentStyles.footer}>
          <Stack
            horizontal
            horizontalAlign='end'
            tokens={{ childrenGap: 10 }}
          >
            <DefaultButton onClick={dismissPanel}>Sluiten</DefaultButton>
          </Stack>
        </div>
      </div>
    </Panel>
  );
};

export default BillitInvoiceDetail;
